import { useCallback, useEffect, useRef, useState } from 'react';
import { useBlocker } from 'react-router';
import { useFeedback } from './components/ui';
import { t } from './i18n';

const jsonEqual = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b);
const isPlainObject = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null && !Array.isArray(v);

/**
 * Borrador de un formulario sobre datos del servidor.
 *
 * Si esos datos cambian mientras se edita (se guardó otra sección de la moto, se tocó el switch
 * de visible, alguien más guardó y la página se refrescó), los campos que la persona no tocó se
 * actualizan y los que sí tocó se conservan. En una lista (colores, puntos…) la unidad es la
 * lista entera: si se tocó, se conserva tal cual.
 *
 * `equal` decide si hay cambios (p. ej. ignorando ids internos o espacios al final).
 */
export function useDraft<T>(initial: T, equal: (a: T, b: T) => boolean = jsonEqual) {
  const [draft, setDraft] = useState(initial);
  const [base, setBase] = useState(initial);
  // Lo que se mandó al guardar: cuando llegue la respuesta, es la referencia para saber qué se
  // escribió después (mientras decía "Guardando…"), y eso se conserva.
  const [sent, setSent] = useState<{ value: T } | null>(null);
  if (base !== initial) {
    // Actualización durante el render (patrón de React para estado derivado de props).
    setBase(initial);
    setDraft(rebase(draft, sent ? sent.value : base, initial, equal));
    if (sent) setSent(null);
  }
  const committedBase = useRef(base);
  useEffect(() => {
    committedBase.current = base;
  }, [base]);
  /** Descarta los cambios: vuelve a lo último que llegó del servidor. */
  const reset = useCallback(() => setDraft(committedBase.current), []);
  /** Llamar tras guardar con éxito, con el borrador que se envió (antes de onSaved). */
  const markSaved = useCallback((value: T) => setSent({ value }), []);
  return { draft, setDraft, dirty: !equal(draft, initial), reset, markSaved };
}

function rebase<T>(draft: T, oldBase: T, newBase: T, equal: (a: T, b: T) => boolean): T {
  if (equal(draft, oldBase)) return newBase;
  if (!isPlainObject(draft) || !isPlainObject(oldBase) || !isPlainObject(newBase)) return draft;
  const merged: Record<string, unknown> = { ...draft };
  for (const key of Object.keys(newBase)) {
    if (jsonEqual(draft[key], oldBase[key])) merged[key] = newBase[key];
  }
  return merged as T;
}

/** Avisa al navegador si se intenta cerrar o recargar la pestaña con cambios sin guardar. */
export function useUnloadGuard(dirty: boolean): void {
  useEffect(() => {
    if (!dirty) return;
    const handler = (e: BeforeUnloadEvent) => e.preventDefault();
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [dirty]);
}

/** Páginas abiertas con cambios sin guardar (para preguntar antes de cerrar sesión). */
const unsaved = new Set<symbol>();
export const hasUnsavedChanges = () => unsaved.size > 0;

/**
 * Pide confirmación antes de ir a otra página del dashboard (menú, "← Motos", un enlace) con
 * cambios sin guardar. Solo puede haber uno activo a la vez: va en la página, no en cada sección.
 */
export function useNavigationGuard(dirty: boolean): void {
  const { confirm } = useFeedback();
  useEffect(() => {
    if (!dirty) return;
    const token = Symbol('sin-guardar');
    unsaved.add(token);
    return () => {
      unsaved.delete(token);
    };
  }, [dirty]);
  const blocker = useBlocker(({ currentLocation, nextLocation }) => dirty && currentLocation.pathname !== nextLocation.pathname);
  const latest = useRef(blocker);
  latest.current = blocker;
  useEffect(() => {
    if (blocker.state !== 'blocked') return;
    void confirm(t('Hay cambios sin guardar. ¿Salir y descartarlos?'), { confirmLabel: t('Salir sin guardar'), danger: true }).then((ok) => {
      const b = latest.current;
      if (b.state !== 'blocked') return;
      if (ok) b.proceed();
      else b.reset();
    });
  }, [blocker.state, confirm]);
}
